/**
 * AI-2 — turn the analyst's deterministic read trace into grounding chips shown under its answer, so the
 * human SEES which real reads the answer rests on (true engine output, not model-claimed). A deal read carries
 * a `ref` (surfaced out-of-band by the server so no internal id ever reached the model), which becomes a
 * deep-link. This is the ONE mapping from a trace entry to a chip — tested, so labels and dedup stay honest.
 */
export interface TraceEntry {
  name: string;
  input: Record<string, unknown>;
  ok: boolean;
  ref?: { no: string; id: number };
}

export interface TraceChip {
  key: string;
  label: string;
  /** present only for a deal read — the chip deep-links to this deal. */
  dealId?: number;
}

/** Plain-Persian names for the read tools; an unknown tool falls back to its raw name rather than vanish. */
const TOOL_LABEL: Record<string, string> = {
  get_deal: 'قرارداد',
  get_credit_status: 'وضعیت اعتبار',
  cash_forecast: 'پیش‌بینی نقدینگی',
  simulate_group_price: 'شبیه‌سازی قیمت',
};

/**
 * The grounding chips for one answer: the reads that ACTUALLY grounded it (ok only), deduped, deal reads
 * deep-linkable. A failed tool call is not grounding, so it is dropped. Same deal read twice ⇒ one chip.
 */
export function traceChips(trace: readonly TraceEntry[]): TraceChip[] {
  const chips: TraceChip[] = [];
  const seen = new Set<string>();
  for (const t of trace) {
    if (!t.ok) continue;
    const base = TOOL_LABEL[t.name] ?? t.name;
    const key = t.ref ? `deal:${t.ref.id}` : t.name;
    if (seen.has(key)) continue;
    seen.add(key);
    chips.push({
      key,
      label: t.ref ? `${base} ${t.ref.no}` : base,
      ...(t.ref ? { dealId: t.ref.id } : {}),
    });
  }
  return chips;
}
